// The `CustomerService` component is a dynamic, interactive interface designed to display
// customer service-related content. It fetches and renders templates, visual types, and FAQs
// based on the current pathname, allowing tailored content to be displayed for each service.
// The component features dropdowns and accordion menus for smooth navigation through service
// categories and sub-items. It supports rendering specific visual components like `BodyContent`
// or `GiftVoucher` depending on the content type and provides loading feedback while data is
// processed. The component also includes breadcrumb functionality for navigation and renders a
// FAQ section to address common queries effectively. Overall, it is built to deliver a responsive
// and user-friendly customer service experience.

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { useRouter } from 'next/navigation';
import Link from 'components/commercetools-ui/atoms/link';
import FaqsAccordian from './faqsAccordian';
import BodyContent from './bodyContent';
import Loading from 'components/commercetools-ui/atoms/loading';
import GiftVoucher from './giftVoucher';
import { CustomerServices, ServiceContent, ServiceFAQ, SubItem, TemplateContents } from 'types/next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import ClubRepairs from './clubRepairs';
import Refunds from './refunds';
import AGStoreLocator from '../ag-club/storeLocator';
// import useGoogleTagManager from 'helpers/hooks/useGoogleTagManager';
import { Breadcrumb } from 'react-instantsearch-hooks-web';
import Recycling from './Recycling';
import PrivacyPolicy from './privacyPolicy';
import ShippingDelivery from './shippingDelivery';
import CookiePolicy from './cookiePolicy';

type customerServiceprops = {
  customerService: CustomerServices[];
  serviceContents: ServiceContent[];
  templateContents: TemplateContents;
  faqs: ServiceFAQ[];
};

interface BreadcrumbItem {
  name: string;
  link: string;
}

const defaultBreadcrumb = [
  { name: 'Home', link: '/' },
  { name: 'Customer Service', link: '/services' },
];

const CustomerService = ({ customerService, serviceContents, templateContents, faqs }: customerServiceprops) => {
  const [currentTemplate, setCurrentTemplate] = useState<any>(null);
  const [currentVisualType, setCurrentVisualType] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const [selectedClubName, setSelectedClubName] = useState('Club Repairs');
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const pathname = usePathname();
  const router = useRouter();
  // const { allPagesEvent } = useGoogleTagManager();

  useEffect(() => {
    setLoading(true);
    // Define a mapping for specific pathnames
    const templateTypeMap: Record<string, string> = {
      '/services/': 'customerService',
      '/services/content/WEEE-Recycling.html': 'recycling',
      '/services/anti-slavery-and-human-trafficking-policy.html': 'antiSlavery',
    };

    if (templateTypeMap[pathname]) {
      if (pathname === '/services/content/WEEE-Recycling.html') {
        const separateTemplateContent = templateContents.recycling;
        const templateData = separateTemplateContent?.find(
          (template: any) => 'templateType' in template && template?.templateType?.toLowerCase() === 'weeerecycling',
        );
        setCurrentTemplate(templateData);
        setCurrentVisualType('recycling');
        setBreadcrumb(defaultBreadcrumb);
      } else {
        const matchedData =
          serviceContents?.find((content: ServiceContent) => content?.templateType === templateTypeMap[pathname]) ||
          null;

        setCurrentTemplate(matchedData);
        setCurrentVisualType('simpleBodyContent');
        setBreadcrumb(defaultBreadcrumb);
      }

      setLoading(false);
      return;
    }
    // Find matching service by pathname
    const contentData = customerService
      ?.flatMap((service: CustomerServices) => service.subItems)
      ?.find((item) => pathname?.includes(item?.link));
    if (contentData) {
      const { templateType = '', visualType = '', name, link } = contentData;

      // Find template data based on visualType and templateType
      const templateData =
        serviceContents?.find(
          (content: ServiceContent) =>
            content?.templateType?.toLowerCase() === templateType.toLowerCase() &&
            content?.visualType?.toLowerCase() === visualType.toLowerCase(),
        ) || null;
      let unqiueTemplateData;
      const unqiueVisualData = templateContents?.[visualType as keyof TemplateContents];
      if (unqiueVisualData && Array.isArray(unqiueVisualData)) {
        unqiueTemplateData = unqiueVisualData?.find(
          (template) =>
            'templateType' in template && template?.templateType.toLowerCase() === templateType.toLowerCase(),
        );
      }
      console.log('UniqueTemplateData', unqiueTemplateData);
      console.log('UniquevisualData', unqiueVisualData);
      const newBreadcrumb = [...defaultBreadcrumb, { name, link }];
      setBreadcrumb(newBreadcrumb);
      setSelectedClubName(name);
      if (templateData) {
        setCurrentTemplate(templateData);
        setCurrentVisualType(visualType);
      } else if (unqiueTemplateData) {
        setCurrentTemplate(unqiueTemplateData);
        setCurrentVisualType(visualType);
      } else {
        console.error(`Template with visualType "${visualType}" and templateType "${templateType}" not found.`);
        setCurrentTemplate(null);
        setCurrentVisualType('');
      }
    } else {
      console.error(`No matching service data found for pathname: ${pathname}`);
      setCurrentTemplate(null);
      setCurrentVisualType('');
    }
    setLoading(false);
  }, [pathname, customerService, serviceContents]);

  const handleBreadcrumbClick = (link: string) => {
    router.push(link);
  };

  const renderVisualComponent = () => {
    switch (currentVisualType) {
      case 'simpleBodyContent':
        return <BodyContent contentData={currentTemplate} />;
      case 'repairs':
        return <ClubRepairs contentData={currentTemplate} />;
      case 'returns':
        return <Refunds contentData={currentTemplate} />;
      case 'vouchers':
        return <GiftVoucher contentData={currentTemplate} />;
      case 'recycling':
        return <Recycling contentData={currentTemplate} />;
      case 'privacyPolicy':
        return <PrivacyPolicy contentData={currentTemplate} />;

      case 'shippingDelivery':
        return <ShippingDelivery contentData={currentTemplate} />;

      case 'cookiePolicy':
        return <CookiePolicy contentData={currentTemplate} />;
      default:
        return <div className="my-15 text-19 font-semibold">No matching component found</div>;
    }
  };

  // useEffect(() => {
  //   allPagesEvent('service');
  // }, []);

  return (
    <div className="px-15 pb-50 pt-30 lg:mx-[43px] lg:px-29">
      <div className="flex items-center space-x-12 pb-30 text-sm">
        {breadcrumb?.map((crumb: BreadcrumbItem, index: number) => (
          <React.Fragment key={index}>
            <span
              onClick={() => handleBreadcrumbClick(crumb.link)}
              className={`my-4 ml-10 cursor-pointer hover:text-[#127e57] ${
                index < breadcrumb.length - 1 ? 'font-normal' : ''
              } ${pathname === crumb.name ? 'font-semibold' : ''}`}
              title={crumb?.link}
            >
              <a href={crumb?.link} className="group relative ml-10 text-14">
                {crumb?.name}
              </a>
            </span>
            {index < breadcrumb.length - 1 && <span className="mx-12 text-[#127e57]">{'>'}</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="gap-x-5 md:flex md:flex-row xl:gap-x-10">
        <div className="w-full pl-15 pr-30 text-sm font-normal md:w-[30%] md:min-w-[285px]">
          {/* Mobile Accordion */}
          <div className="block pb-30 md:hidden">
            <button
              onClick={toggleDropdown}
              className="relative h-40 w-full rounded border px-14 text-left font-semibold"
            >
              {selectedClubName}
              <span className="absolute right-16 top-1/2 -translate-y-1/2 transform">
                {isDropdownOpen ? (
                  <ChevronUpIcon className="h-15 w-15 text-[#127e57]" />
                ) : (
                  <ChevronDownIcon className="h-15 w-15 text-[#127e57]" />
                )}
              </span>
            </button>
            {isDropdownOpen && (
              <ul className="border-x border-b">
                {customerService.map((service: CustomerServices, index: number) => {
                  return (
                    <>
                      <li
                        key={index}
                        className={`my-3 ml-10 flex cursor-pointer items-center px-3 py-4 text-16 font-bold leading-5 hover:text-[#127e57]
                        `}
                      >
                        {service?.serviceName}
                      </li>
                      {service?.subItems?.map((items: SubItem, index: number) => {
                        const isActive = pathname === items?.link;
                        return (
                          <li
                            key={index}
                            className={`my-3 ml-30 flex cursor-pointer items-center px-3 py-4 hover:text-[#127e57] ${
                              isActive ? 'text-[#127e57]' : ''
                            }`}
                            onClick={() => {
                              router.push(items?.link);
                              setSelectedClubName(items?.name);
                            }}
                          >
                            {items?.name}
                          </li>
                        );
                      })}
                    </>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="accordion-section mb-40 hidden border-t border-[#d1c9c4] md:block">
            {customerService?.map((service: CustomerServices, index: number) => (
              <AccordionBtn
                key={index}
                variant="plusAndMinus"
                className="col-span-2 border-b border-[#d1c9c4] font-body text-16 font-bold leading-5 hover:text-[#127e57] md:mt-0"
                closedSectionTitle={service.serviceName}
                buttonClassName="py-15 pl-0 md:pr-12"
                panelClassName="pb-20 md:pb-6 text-primary-black text-14 font-semibold leading-5 font-body"
                // defaultOpen={service?.serviceName === selectedClubName}
              >
                <ul>
                  {service?.subItems?.map((item: SubItem, subIndex: number) => (
                    <Link link={item?.link} key={subIndex}>
                      <li key={subIndex} className="flex cursor-pointer items-center pb-10 hover:text-[#127e57]">
                        <span className="mr-10 pt-1 text-16 font-semibold text-[#127e57]">{'>'}</span>
                        {item.name}
                      </li>
                    </Link>
                  ))}
                </ul>
              </AccordionBtn>
            ))}
          </div>
          <AGStoreLocator />
        </div>
        {loading ? (
          <>
            <Loading />
          </>
        ) : (
          <main role="main" className="customservices w-full pt-1">
            {renderVisualComponent()}
            <FaqsAccordian faqs={faqs} />
          </main>
        )}
      </div>
    </div>
  );
};

export default CustomerService;
